import React from "react";
import PropTypes from "prop-types";
import AmountContainer from "app/pages/.shared/AmountContainer";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import Separator from "app/pages/.shared/Separator";
import isEmpty from "lodash/isEmpty";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import {
	PAYMENT_METHODS,
	SPECIAL_OFFER_TYPES,
	TRANSPORTATION_TYPES,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { paymentTypesValues } from "app/utils/propTypes";

import "./ExtraPriceDetails.scss";
import { checkIfActivityIncluded } from "app/utils/utils";

const ExtraPriceDetails = ({
	transfer,
	includedTransfer,
	insurance,
	board,
	flight,
	accommodation,
	luggageTotalPrice,
	luggage,
	totalUsableCredit,
	totalUsableCreditNote,
	promotionValue,
	coutCreditCasino,
	activities,
	paymentType,
	usableCouponsStatus,
	isPagePayment,
	specialOffer,
	isSpecialOfferActive,
}) => {
	const accommodationNode = !isEmpty(accommodation) && !accommodation.included && (
		<div>
			<div className="extra-price-detail__item" data-cy="price-detail-room">
				<div className="extra-price-detail__label">
					<FormattedMessage id="price.detail.room.upgrade.label" />
				</div>
				<div className="extra-price-detail__value">
					<AmountContainer
						prefix={<AmountSign sign="+" />}
						amount={accommodation.upgradePrice || 0}
					/>
				</div>
			</div>
			<div className="extra-price-detail__item">
				<div className="extra-price-detail__label">
					<ul className="extra-price-detail__details">
						{accommodation.items.map((item, index) => {
							return (
								<li key={index}>
									{item.quantity} x {item.label}
								</li>
							);
						})}
					</ul>
				</div>
			</div>
		</div>
	);

	const boardNode = !isEmpty(board) && (
		<div className="extra-price-detail__item" data-cy="price-detail-board">
			<div className="extra-price-detail__label">
				{board.included ? (
					board.label
				) : (
					<FormattedMessage
						values={{
							board: board.label,
						}}
						id="price.detail.board.upgrade.label"
					/>
				)}
			</div>
			<div className="extra-price-detail__value">
				{board.included ? (
					<FormattedMessage id="general.included" />
				) : (
					<AmountContainer
						prefix={<AmountSign sign="+" />}
						amount={board.upgradePrice || 0}
					/>
				)}
			</div>
		</div>
	);

	const isTransportationTypeWithoutFlight = flight.type !== TRANSPORTATION_TYPES.FLIGHT;

	const transportPositiveUpgradePriceLabel = isTransportationTypeWithoutFlight ? (
		<FormattedMessage id="price.detail.transport.upgrade.label" />
	) : (
		<FormattedMessage id="price.detail.flight.upgrade.label" />
	);

	const transportNegativeUpgradePriceLabel = isTransportationTypeWithoutFlight ? (
		<FormattedMessage id="price.detail.transport.reduction.label" />
	) : (
		<FormattedMessage id="price.detail.flight.reduction.label" />
	);

	const flightNode = !isEmpty(flight) && !flight.included && flight.upgradePrice !== 0 && (
		<div className="extra-price-detail__item" data-cy="price-detail-transport">
			<div className="extra-price-detail__label">
				{flight.upgradePrice < 0
					? transportNegativeUpgradePriceLabel
					: transportPositiveUpgradePriceLabel}
			</div>
			<div className="extra-price-detail__value">
				<AmountContainer
					prefix={flight.upgradePrice < 0 ? "" : <AmountSign sign="+" />}
					amount={flight.upgradePrice || 0}
				/>
			</div>
		</div>
	);

	const luggageNode = luggageTotalPrice > 0 && (
		<div className="extra-price-detail__item" data-cy="price-detail-luggage">
			<div className="extra-price-detail__label">
				<FormattedPlural
					value={luggage}
					one={
						<FormattedMessage
							id="price.detail.luggage.label.singular"
							values={{
								count: luggage,
							}}
						/>
					}
					other={
						<FormattedMessage
							id="price.detail.luggage.label.plural"
							values={{
								count: luggage,
							}}
						/>
					}
				/>
			</div>
			<div className="extra-price-detail__value">
				<AmountContainer prefix={<AmountSign sign="+" />} amount={luggageTotalPrice} />
			</div>
		</div>
	);

	const hasIncludedTransfer = includedTransfer !== undefined;

	const transferNode = !isEmpty(transfer) && transfer.code !== "NO_TRANSFER" && (
		<div className="extra-price-detail__item" data-cy="price-detail-transfert">
			<div className="extra-price-detail__label">
				{hasIncludedTransfer && transfer.included ? transfer.vehicule : false}

				{hasIncludedTransfer && !transfer.included && (
					<FormattedMessage
						id="price.detail.transfer.upgrade.label"
						values={{
							transfer: transfer.vehicule,
						}}
					/>
				)}

				{!hasIncludedTransfer && transfer.price > 0 ? transfer.vehicule : false}
			</div>
			<div className="extra-price-detail__value">
				{hasIncludedTransfer && transfer.included && (
					<FormattedMessage id="general.included" />
				)}

				{hasIncludedTransfer && !transfer.included && (
					<AmountContainer
						prefix={<AmountSign sign="+" />}
						amount={transfer.upgradePrice}
					/>
				)}

				{!hasIncludedTransfer && transfer.price > 0 && (
					<AmountContainer prefix={<AmountSign sign="+" />} amount={transfer.price} />
				)}
			</div>
		</div>
	);

	const insuranceNode = !isEmpty(insurance) && isPagePayment && (
		<div className="extra-price-detail__item" data-cy="price-detail-insurance">
			<div className="extra-price-detail__label">{insurance.name}</div>
			<div className="extra-price-detail__value">
				<AmountContainer prefix={<AmountSign sign="+" />} amount={insurance.price} />
			</div>
		</div>
	);

	const coutCreditNode = (paymentType === PAYMENT_METHODS.CASINO ||
		paymentType === PAYMENT_METHODS.PLEDG) &&
		coutCreditCasino > 0 && (
			<div className="extra-price-detail__item" data-cy="price-detail-cout-credit">
				<div className="extra-price-detail__label">
					<FormattedMessage id="price.detail.cout.credit.label" />
				</div>
				<div className="extra-price-detail__value">
					<AmountContainer prefix={<AmountSign sign="+" />} amount={coutCreditCasino} />
				</div>
			</div>
		);

	const promotion = promotionValue > 0 && isPagePayment && (
		<div className="extra-price-detail__item" data-cy="price-detail-promotion-code">
			<div className="extra-price-detail__label">
				<FormattedMessage id="price.detail.promo.code.label" />
			</div>
			<div className="extra-price-detail__value">
				<AmountContainer prefix={<AmountSign sign="-" />} amount={promotionValue} />
			</div>
		</div>
	);

	const isCalculatingPrices =
		(usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS) &&
		isPagePayment;

	const credits = (totalUsableCredit > 0 || isCalculatingPrices) && isPagePayment && (
		<div className="extra-price-detail__item" data-cy="price-detail-credits">
			<div className="extra-price-detail__label">
				<FormattedMessage id="price.detail.credits.label" />
			</div>
			<div className="extra-price-detail__value">
				{isCalculatingPrices ? (
					<LoaderBar width={80} />
				) : (
					<AmountContainer prefix={<AmountSign sign="-" />} amount={totalUsableCredit} />
				)}
			</div>
		</div>
	);

	const creditNote = (totalUsableCreditNote > 0 || isCalculatingPrices) && isPagePayment && (
		<div className="extra-price-detail__item" data-cy="price-detail-creditnotes">
			<div className="extra-price-detail__label">
				<FormattedMessage id="price.detail.creditnotes.label" />
			</div>
			<div className="extra-price-detail__value">
				{isCalculatingPrices ? (
					<LoaderBar width={80} />
				) : (
					<AmountContainer
						prefix={<AmountSign sign="-" />}
						amount={totalUsableCreditNote}
					/>
				)}
			</div>
		</div>
	);

	const activityNode = activities.map(activity => {
		const total = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;

			const guestPrices = guest.pricePerQuantity[guestCount] || {};
			const guestPrice = guestPrices.price || 0;
			const guestDiscount = guestPrices.discount || 0;

			return acc + guestPrice + guestDiscount;
		}, 0);

		const totalDiscount = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;

			const guestPrices = guest.pricePerQuantity[guestCount] || {};
			const guestDiscount = guestPrices.discount || 0;

			return acc + guestDiscount;
		}, 0);

		const hasIncludedGuest = checkIfActivityIncluded(activity.guests);

		const removedGuests = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;
			const includedGuests = guest.includedGuest;

			return acc + includedGuests - guestCount;
		}, 0);

		return (
			<React.Fragment key={activity.code}>
				<div
					className="extra-price-detail__item"
					data-cy={`price-detail-activity-${activity.code}`}
				>
					<div className="extra-price-detail__label">
						<FormattedMessage
							id="price.detail.activity.label"
							values={{
								activityName: activity.name,
								activityDate: <FormattedDate value={activity.date} />,
							}}
						/>
					</div>
					{!hasIncludedGuest && (
						<div className="extra-price-detail__value">
							<AmountContainer prefix={<AmountSign sign="+" />} amount={total} />
						</div>
					)}
				</div>
				<ul
					className="extra-price-detail__details"
					data-cy={`price-detail-activity-${activity.code}-guests`}
				>
					{activity.guests.map((guest, index) => {
						const hasOnlyMinAge = guest.minAge && !guest.maxAge;
						const hasOnlyMaxAge = !guest.minAge && guest.maxAge;
						const hasMinAgeAndMaxAge = guest.minAge && guest.maxAge;
						const isForAllAges = !guest.minAge && !guest.maxAge;

						const guestCount = guest.guestsCount;

						const guestPrices = guest.pricePerQuantity[guestCount] || {};
						const guestPrice = guestPrices.price || 0;

						let guestLabel = "";

						if (hasOnlyMinAge) {
							guestLabel = (
								<React.Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.min.label"
										values={{
											minAge: guest.minAge,
										}}
									/>
								</React.Fragment>
							);
						} else if (hasMinAgeAndMaxAge) {
							guestLabel = (
								<React.Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.range.label"
										values={{
											minAge: guest.minAge,
											maxAge: guest.maxAge,
										}}
									/>
								</React.Fragment>
							);
						} else if (hasOnlyMaxAge) {
							guestLabel = (
								<React.Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.max.label"
										values={{
											maxAge: guest.maxAge,
										}}
									/>
								</React.Fragment>
							);
						} else if (isForAllAges) {
							guestLabel = (
								<div className="quotation-activity__age-label">
									<FormattedMessage id="price.detail.activity.guests.label" />
								</div>
							);
						}

						return (
							guestCount > 0 && (
								<li key={index} className="extra-price-detail__subitem">
									<div className="extra-price-detail__label">
										{guestCount} x {guestLabel}
									</div>
									{hasIncludedGuest && (
										<div className="extra-price-detail__value">
											{guestPrice > 0 ? (
												<AmountContainer
													prefix={<AmountSign sign="+" />}
													amount={guestPrice}
												/>
											) : (
												<FormattedMessage id="general.included" />
											)}
										</div>
									)}
								</li>
							)
						);
					})}

					{totalDiscount < 0 && hasIncludedGuest && (
						<li key="discount" className="extra-price-detail__subitem">
							<div className="extra-price-detail__label">
								{removedGuests} x{" "}
								<FormattedMessage id="price.detail.activity.discount.guests.label" />
							</div>
							<div className="extra-price-detail__value">
								<AmountContainer
									prefix={<AmountSign sign="-" />}
									amount={Math.abs(totalDiscount)}
								/>
							</div>
						</li>
					)}
				</ul>
			</React.Fragment>
		);
	});

	const showFreeCancellationIncluded =
		isSpecialOfferActive && specialOffer.type === SPECIAL_OFFER_TYPES.REFUNDABLE;

	return (
		<div className="extra-price-detail">
			{accommodationNode}
			{boardNode}
			{flightNode}
			{luggageNode}
			{transferNode}
			{activityNode}
			{insuranceNode}
			{showFreeCancellationIncluded && (
				<div className="extra-price-detail__item">
					<div className="extra-price-detail__label">
						<FormattedMessage id="price.detail.free.cancellation.label" />
					</div>
					<div className="extra-price-detail__value">
						<FormattedMessage id="general.included" />
					</div>
				</div>
			)}
			{coutCreditNode}
			{promotion}
			{credits}
			{creditNote}
			<Separator design="middle" />
		</div>
	);
};

ExtraPriceDetails.defaultProps = {
	shop: "",
	transfer: {},
	insurance: {},
	board: {},
	flight: {},
	accommodation: {},
	coutCreditCasino: 0,
	activities: [],
	specialOffer: {},
};

ExtraPriceDetails.propTypes = {
	coutCreditCasino: PropTypes.number,
	board: PropTypes.object,
	accommodation: PropTypes.object,
	flight: PropTypes.object,
	insurance: PropTypes.object,
	transfer: PropTypes.object,
	includedTransfer: PropTypes.object,
	luggageTotalPrice: PropTypes.number,
	luggage: PropTypes.number,
	totalUsableCredit: PropTypes.number,
	totalUsableCreditNote: PropTypes.number,
	promotionValue: PropTypes.number,
	activities: PropTypes.arrayOf(PropTypes.object),
	paymentType: paymentTypesValues,
	usableCouponsStatus: PropTypes.string,
	isPagePayment: PropTypes.bool,
	isSpecialOfferActive: PropTypes.bool,
	specialOffer: PropTypes.object,
};

export default React.memo(ExtraPriceDetails);
